/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import { RichText } from '@/components/common/RichText';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Sparkles, TrendingUp, AlertTriangle, Target, CheckCircle2, RefreshCw, Users, CalendarClock, DollarSign, Ban, ArrowUpRight, Loader2 } from 'lucide-react';
import { getDoctorInsights } from '@/lib/ai_clinical';
import { cn } from '@/lib/utils';

const KpiTile = ({ icon: Icon, label, value, sub, tone = 'primary' }) => {
  const toneCls = {
    primary: 'bg-primary/10 text-primary',
    accent: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    warning: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    danger: 'bg-red-500/10 text-red-600 dark:text-red-400',
    success: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  }[tone];
  return (
    <Card>
      <CardContent className="p-4">
        <div className={cn('inline-flex items-center justify-center h-9 w-9 rounded-xl', toneCls)}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="mt-3 text-2xl font-display font-semibold tracking-tight" data-testid={`kpi-${label}`}>{value}</div>
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">{label}</div>
        {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
};

const priorityBadge = (p) => {
  const meta = {
    high:   'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30',
    medium: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
    low:    'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
  }[p] || 'bg-muted text-muted-foreground';
  return meta;
};

const DoctorInsightsPage = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);

  const load = async (fresh = false) => {
    if (fresh) setRefreshing(true); else setLoading(true);
    try {
      const res = await getDoctorInsights();
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossibile caricare gli insight');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(false); }, []);

  const stats = data?.stats;
  const insights = data?.result;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 lg:py-10" data-testid="doctor-insights-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-primary"><Sparkles className="h-5 w-5" /><span className="text-xs uppercase tracking-wider font-bold">Nexi AI Business Intelligence</span></div>
          <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight mt-1">Insight & Analytics</h1>
          <p className="text-muted-foreground text-sm md:text-base mt-1 max-w-xl">Analisi automatica degli ultimi 30 giorni con raccomandazioni concrete per far crescere la tua attività.</p>
        </div>
        <Button variant="outline" onClick={() => load(true)} disabled={refreshing} data-testid="insights-refresh-btn">
          {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Aggiorna
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Skeleton className="h-32" /><Skeleton className="h-32" /><Skeleton className="h-32" /><Skeleton className="h-32" />
        </div>
      ) : (
        <div className="space-y-5">
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="insights-kpis">
              <KpiTile icon={CalendarClock} label="Appuntamenti" value={stats.total_appointments} sub={`${stats.completed} completati`} tone="primary" />
              <KpiTile icon={Users} label="Urgenti" value={stats.urgent} sub="nelle ultime 30gg" tone="warning" />
              <KpiTile icon={Ban} label="No-show" value={stats.no_show + stats.cancelled} sub={`${stats.no_show} no-show, ${stats.cancelled} annullati`} tone="danger" />
              <KpiTile icon={DollarSign} label="Fatturato" value={`€ ${(stats.revenue_eur || 0).toFixed(2)}`} sub={`${stats.paid} visite pagate`} tone="success" />
            </div>
          )}

          {insights?.summary && (
            <Card>
              <CardContent className="pt-5">
                <RichText className="text-sm" text={insights.summary} />
              </CardContent>
            </Card>
          )}

          {insights?.highlights?.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" />Punti chiave</CardTitle></CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {insights.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      {h}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {insights?.opportunities?.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Target className="h-4 w-4 text-primary" />Opportunità</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {insights.opportunities.map((o) => (
                  <div key={o.title} className="rounded-xl border border-border/60 p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="font-semibold text-sm">{o.title}</div>
                      <Badge variant="outline" className={cn('ml-auto', priorityBadge(o.priority))}>Priorità {o.priority}</Badge>
                    </div>
                    {o.detail && <p className="text-xs text-muted-foreground mt-1.5">{o.detail}</p>}
                    {o.estimated_impact && <div className="text-xs mt-2 text-primary flex items-center gap-1"><ArrowUpRight className="h-3 w-3" />{o.estimated_impact}</div>}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {insights?.risks?.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" />Rischi</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {insights.risks.map((r) => (
                  <div key={r.title} className="rounded-xl border border-border/60 p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="font-semibold text-sm">{r.title}</div>
                      <Badge variant="outline" className={cn('ml-auto', priorityBadge(r.priority))}>{r.priority}</Badge>
                    </div>
                    {r.detail && <p className="text-xs text-muted-foreground mt-1.5">{r.detail}</p>}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {insights?.next_actions?.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" />Prossime azioni consigliate</CardTitle></CardHeader>
              <CardContent>
                <ol className="list-decimal pl-5 text-sm space-y-1">
                  {insights.next_actions.map((a) => <li key={a}>{a}</li>)}
                </ol>
              </CardContent>
            </Card>
          )}

          {data?.cached && (
            <Alert>
              <AlertDescription className="text-xs">Insight in cache (rigenerati ogni 6 ore per efficienza).</AlertDescription>
            </Alert>
          )}
        </div>
      )}
    </div>
  );
};

export default DoctorInsightsPage;
